import React, { useMemo } from "react";
import "./styles/base.css";
import "./styles/formGrid.css";
import AppShell from "./app/AppShell";
import Starfield from "./app/visual/Starfield";
import Sidebar from "./app/shell/Sidebar";
import BottomNav from "./app/shell/BottomNav";
import TopBar from "./app/shell/TopBar";
import LanguageSwitcher from "./app/shell/LanguageSwitcher";
import LandingPage from "./app/landing/LandingPage";
import PricingSection from "./app/pricing/PricingSection";
import TermsPage from "./app/legal/TermsPage";
import PrivacyPage from "./app/legal/PrivacyPage";
import ContactPage from "./app/contact/ContactPage";
import LegalFooterLinks from "./app/legal/LegalFooterLinks";
import AboutPage from "./features/about/AboutPage";
import { initGa4, gaSetUserFromAuth, gaTrackPageView, gaTrackEvent } from "./analytics/ga4";
import { getAuthContext, runtimeConfig } from "./config/runtime";
import { mountApp } from "./entrypoints/app";
import LoginPage from "./features/auth/LoginPage";
import EmailVerificationRequiredPage from "./features/auth/EmailVerificationRequiredPage";
import CreditsInsightsPage from "./features/credits/components/CreditsInsightsPage";
import ReadingHistoryPage from "./features/reading-history/ReadingHistoryPage";
import { DashboardHome } from "./features/dashboard";
import SettingsPage from "./features/settings/SettingsPage";
import { LocaleProvider, useI18n } from "./features/i18n/LocaleProvider";
import { readBirthProfile } from "./features/profile/storage";
import { getDashboardGreeting, formatDashboardDate, formatDashboardShortDate, getMoonSignLabel, getSunSignLabel } from "./features/dashboard/meta";
import { TimezoneProvider, useDisplayTimezone } from "./features/timezone/TimezoneProvider";
import { ThemeProvider, useAccent } from "./features/theme/ThemeProvider";
import TarotReadingPage from "./features/tarot/components/TarotReadingPage";
import WeddingCompatibilityPage from "./features/wedding/components/WeddingCompatibilityPage";
import CoupleMatchPage from "./features/couple-match/components/CoupleMatchPage";
import ChineseZodiacPage from "./features/zodiac/chinese/ChineseZodiacPage";
import WesternAstrologyPage from "./features/zodiac/western/WesternAstrology";
import PaymentPage from "./features/payments/PaymentPage";
import PublicPageShell from "./ui/PublicPageShell";
initGa4();

const APP_HELPERS = {
  getDashboardGreeting,
  formatDashboardDate,
  formatDashboardShortDate,
  getMoonSignLabel,
  getSunSignLabel,
};

function getBirthProfileDefaults(user) {
  const localProfile = readBirthProfile();
  return {
    birthDate: user?.birthDate || localProfile.birthDate || "",
    birthTime: user?.birthTime || localProfile.birthTime || "",
    birthPlace: user?.birthPlace || localProfile.birthPlace || "",
  };
}

function WesternAstrology(props) {
  const { t, locale } = useI18n();
  return (
    <WesternAstrologyPage
      {...props}
      t={t}
      locale={locale}
      getBirthProfileDefaults={getBirthProfileDefaults}
      authUser={runtimeConfig.authContext.user}
    />
  );
}

function ChineseZodiac(props) {
  const { t } = useI18n();
  return <ChineseZodiacPage {...props} t={t} authUser={runtimeConfig.authContext.user} />;
}

function WeddingCompatibility(props) {
  const { t } = useI18n();
  return <WeddingCompatibilityPage {...props} t={t} authUser={runtimeConfig.authContext.user} />;
}

function CoupleMatch(props) {
  const { t } = useI18n();
  return <CoupleMatchPage {...props} t={t} authUser={runtimeConfig.authContext.user} />;
}

function AppContent() {
  const { locale, setLocale, t: translate } = useI18n();
  const { timezone, getGmtLabel, formatInTimezone } = useDisplayTimezone();
  const acc = useAccent();

  const components = useMemo(
    () => ({
      Starfield,
      Sidebar,
      BottomNav,
      TopBar,
      DashboardHome: (props) => <DashboardHome {...props} t={translate} />,
      WesternAstrology,
      ChineseZodiac,
      TarotReading: (props) => <TarotReadingPage {...props} t={translate} authUser={runtimeConfig.authContext.user} />,
      WeddingCompatibility,
      CoupleMatch,
      Settings: SettingsPage,
      PaymentPage,
      CreditsInsightsPage: (props) => <CreditsInsightsPage {...props} t={translate} />,
      ReadingHistoryPage: (props) => <ReadingHistoryPage {...props} t={translate} />,
      PricingSection,
      PublicPageShell,
      LanguageSwitcher,
      AboutPage: (props) => <AboutPage {...props} t={translate} />,
      TermsPage,
      PrivacyPage,
      ContactPage,
      LoginPage: (props) => <LoginPage {...props} t={translate} />,
      EmailVerificationRequiredPage,
      LandingPage,
      LegalFooterLinks,
    }),
    [translate]
  );

  return (
    <AppShell
      locale={locale}
      setLocale={setLocale}
      acc={acc}
      authContext={runtimeConfig.authContext}
      initialPage={runtimeConfig.initialPage}
      t={translate}
      timezone={timezone}
      timezoneLabel={getGmtLabel(locale)}
      formatInTimezone={formatInTimezone}
      helpers={APP_HELPERS}
      components={components}
    />
  );
}

function readServerHtmlLocale() {
  if (typeof window === "undefined") return null;
  const raw = window.ECLIPTICA_HTML_LOCALE;
  return raw != null && String(raw).trim() !== "" ? String(raw).trim() : null;
}

export function MainApp() {
  return (
    <LocaleProvider serverLocale={readServerHtmlLocale()}>
      <TimezoneProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </TimezoneProvider>
    </LocaleProvider>
  );
}

const guideBoot =
  typeof document !== "undefined"
    ? String(document.documentElement.getAttribute("data-ecliptica-boot") || "").trim()
    : "";

if (guideBoot === "guide_lite") {
  gaTrackPageView({
    pagePath: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "",
    pageTitle: typeof document !== "undefined" ? document.title : "",
    screenClass: "guide",
  });
  const slug =
    typeof document !== "undefined"
      ? String(document.documentElement.getAttribute("data-ecliptica-guide-slug") || "").trim()
      : "";
  if (slug) gaTrackEvent("guide_view", { guide_slug: slug });
} else if (guideBoot === "article_lite") {
  gaTrackPageView({
    pagePath: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "",
    pageTitle: typeof document !== "undefined" ? document.title : "",
    screenClass: "article",
  });
  Promise.all([
    import("./articles/cosmicBackground"),
    import("./articles/articleReactions"),
  ]).then(([{ initArticleCosmicBackground }, { initArticleReactions }]) => {
    initArticleCosmicBackground();
    initArticleReactions();
  });
} else {
  gaSetUserFromAuth(getAuthContext());
  mountApp(MainApp);
}
